import React, { useEffect, useRef } from 'react'

// Shared canvas bar renderer for both the voice-message waveform and the live
// recorder meter (they use the exact same routine — don't duplicate it).
//
// Draws `peaks` (values ~0..1) as centered vertical bars. Bars before
// `progress` (0..1 of the width) are the accent colour (--colorPrimary); the
// rest are currentColor at ~35% opacity. Colours are read from the canvas's
// computed style so it tracks the theme. Handles devicePixelRatio.
//
// ponytail: no ResizeObserver — the canvas uses its live clientWidth on every
// draw, and draws are driven by the player's currentTime/duration state (or the
// recorder's per-frame level), so it's correct whenever it actually paints; a
// window resize of a never-touched bar just isn't worth an observer.
export function drawBars(
  canvas: HTMLCanvasElement,
  peaks: Float32Array | ArrayLike<number>,
  progress: number
): void {
  const ctx = canvas.getContext('2d')
  if (ctx == null) {
    return
  }
  const dpr = window.devicePixelRatio || 1
  const cssW = canvas.clientWidth || 200
  const cssH = canvas.clientHeight || 32
  if (canvas.width !== Math.round(cssW * dpr)) {
    canvas.width = Math.round(cssW * dpr)
  }
  if (canvas.height !== Math.round(cssH * dpr)) {
    canvas.height = Math.round(cssH * dpr)
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, cssW, cssH)

  const style = getComputedStyle(canvas)
  const played = style.getPropertyValue('--colorPrimary').trim() || style.color
  const unplayed = style.color

  const n = peaks.length
  if (n === 0) {
    return
  }
  const barW = cssW / n
  const gap = Math.min(1, barW * 0.25)
  const playedBars = progress * n
  for (let i = 0; i < n; i++) {
    const h = Math.max(1, peaks[i] * cssH)
    const isPlayed = i < playedBars
    ctx.globalAlpha = isPlayed ? 1 : 0.35
    ctx.fillStyle = isPlayed ? played : unplayed
    ctx.fillRect(i * barW, (cssH - h) / 2, Math.max(1, barW - gap), h)
  }
  ctx.globalAlpha = 1
}

/** Canvas that redraws `peaks` at `progress` whenever either changes. */
export function WaveformCanvas({
  peaks,
  progress,
  className,
}: {
  peaks: Float32Array
  progress: number
  className?: string
}) {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    if (ref.current != null) {
      drawBars(ref.current, peaks, progress)
    }
  }, [peaks, progress])
  return <canvas ref={ref} className={className} aria-hidden='true' />
}
